import classNames from "classnames";
import { Link } from "../../common/components/Link";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import type ComponentTypes from "../componentTypes";
import { type CMSTeasersProps } from "./CMSTeaserContainer/CMSTeasers";
import { type CMSTeaserProps } from "../model";
import { type CMSBannerProps } from "./CMSBanner";
import { type CMSRotatingBannersProps } from "./CMSRotatingBanners";
import {
  type CMSAccordionContainerDTO,
  type CMSAccordionDTO,
  type CMSChipLinkDTO,
  type CMSCardComponentDTO,
  type CMSColumnDTO,
  type CMSColumnLinksDTO,
  type CMSColumnsContainerDTO,
  type CMSHomepagePersonalizationDTO,
  type CMSPhotoDTO,
  type CMSPhotoGalleryDTO,
  type CMSQuoteDTO,
  type CMSStandaloneImageDTO,
  type CMSTableDTO,
  type LoginCallDTO,
  type SchaefflerFormCheckboxFieldDTO,
  type SchaefflerFormDTO,
  type SchaefflerFormHiddenFieldDTO,
  type SchaefflerFormSelectFieldDTO,
  type SchaefflerFormTextFieldDTO,
} from "../dto";
import { type CMSFilterableTileContainerDTO } from "./CMSFilterableTileContainer/dto";
import { renderCMSComponent } from "../componentFactory";
import { type BEButtonSizes, getButtonSize, getButtonType } from "~/CMSComponents/CMSComponentHelpers";
import { Button } from "~/common/components/Button/Button";
import type { CMSPageNavigationComponentProps } from "~/CMSComponents/components/CMSPageNavigation";
import type { CMSNewsletterConsentDTO } from "~/CMSComponents/components/CMSNewsletterConsent";
import type { EwellixPageNavigationComponentDTO } from "~/CMSComponents/components/EwellixPageNavigationComponent/types";
import { Stack, styled } from "@mui/material";
import { makeExternallyStyledBaseElement, makeExternallyStyledElement } from "../CMSExternalStyleHelper";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";

export type CMSComponentProps =
  | CMSSectionComponentProps
  | CMSStandaloneImageDTO
  | CMSParagraphProps
  | CMSListProps
  | CMSButtonProps
  | CMSLinkProps
  | CMSTeasersProps
  | CMSTeaserProps
  | CMSBannerProps
  | CMSRotatingBannersProps
  | CMSFilterableTileContainerDTO
  | LoginCallDTO
  | CMSQuoteDTO
  | CMSColumnsContainerDTO
  | CMSColumnDTO
  | CMSPageNavigationComponentProps
  | CMSNewsletterConsentDTO
  | CMSPhotoDTO
  | CMSPhotoGalleryDTO
  | CMSColumnLinksDTO
  | CMSAccordionContainerDTO
  | CMSAccordionDTO
  | CMSTableDTO
  | EwellixPageNavigationComponentDTO
  | SchaefflerFormDTO
  | SchaefflerFormHiddenFieldDTO
  | SchaefflerFormSelectFieldDTO
  | SchaefflerFormTextFieldDTO
  | SchaefflerFormCheckboxFieldDTO
  | CMSCardComponentDTO
  | CMSHomepagePersonalizationDTO
  | CMSChipLinkDTO;

export interface CMSProps {
  id: string;
  className?: string;
  content?: string;
  fullWidth?: boolean;
}

export interface CMSParagraphProps extends CMSProps {
  rootStyles?: string;
  type: ComponentTypes.CMSParagraph;
}

export interface CMSListProps extends CMSProps {
  type: ComponentTypes.CMSList;
  subHeadline?: string;
}

export interface CMSButtonProps extends CMSProps {
  type: ComponentTypes.CMSButton;
  buttonType?: "PRIMARY" | "SECONDARY" | "TEXT";
  url?: string;
  label?: string;
  size?: BEButtonSizes;
  openInExternal?: boolean;
}

export interface CMSLinkProps extends CMSProps {
  type: ComponentTypes.CMSLink;
  url?: string;
  title?: string;
  target?: string;
  label?: string;
}

export interface CMSSectionComponentProps extends CMSProps {
  id: string;
  type: ComponentTypes.CMSSection;
  headlineTag?: string;
  htmlId?: string;
  headline?: string;
  subHeadline?: string;
  subListHeadline?: string;
  elements: CMSComponentProps[];
  rootStyles?: string;
  headlineStyles?: string;
  subHeadlineStyles?: string;
}

/**
 * @see https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml
 * @param html
 */
export function createMarkup(html: string) {
  html = cleanupText(html);
  return { __html: html };
}

export function cleanupText(text: string): string;
export function cleanupText(text: undefined): undefined;
export function cleanupText(text: string | undefined): string | undefined;
export function cleanupText(text: null): null;
export function cleanupText(text: string | undefined | null) {
  return text ? text.replace(/\\([()])/g, "$1") : text;
}

export function renderAsHTML(html: string) {
  return <div className={"scha__cmssection__html"} dangerouslySetInnerHTML={createMarkup(html)} />;
}

export const CMSParagraph = styled(
  ({ content = "", className }: CMSParagraphProps) => {
    return (
      <div
        className={classNames(
          "scha__cmssection__html scha__cmssection__sub--paragraph copy_left jsp__user-generated-content",
          className,
        )}
        dangerouslySetInnerHTML={createMarkup(content)}
      />
    );
  },
  { shouldForwardProp: (prop) => prop !== "rootStyles" },
)(({ rootStyles }) => rootStyles);

export const CMSList = (props: CMSListProps) => {
  const { content, subHeadline, className } = props;
  return (
    <div className={classNames("scha__cms scha__cmssection__sub--list", className)}>
      {/* This h3 is styled like a h6 in the designs */}
      {subHeadline && !!subHeadline.length && (
        <h3
          className={"scha__cmssection__sub-element__headline headline_h6_left"}
          dangerouslySetInnerHTML={createMarkup(subHeadline)}
        />
      )}
      {content && !!content.length && renderAsHTML(content)}
    </div>
  );
};

export const CMSButton = (props: CMSButtonProps) => {
  const { url, label, className, buttonType, size, openInExternal } = props;

  const buttonProps = {
    ...getButtonSize(size ?? "BIG"),
    ...getButtonType(buttonType ?? "primary"),
  };

  // @ts-ignore
  return (
    <div className="scha__cms">
      <Button
        component={"a"}
        className={classNames("scha__cmssection__sub--button scha__cmssection__button", className)}
        href={url}
        {...buttonProps}
        target={openInExternal ? "_blank" : undefined}
      >
        {label}
      </Button>
    </div>
  );
};

export const CMSLink = (props: CMSLinkProps) => {
  const { url, label, target, className } = props;
  return (
    <Link
      className={classNames("scha__cms scha__cmssection__sub--link", className)}
      href={url}
      target={target}
      iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronRight} />}
    >
      {label}
    </Link>
  );
};

export const CMSSection = ({
  headlineTag,
  elements,
  headline,
  subHeadline,
  htmlId,
  className,
  rootStyles,
  headlineStyles,
  subHeadlineStyles,
}: CMSSectionComponentProps) => {
  return (
    <RootElement className={className} rootStyles={rootStyles}>
      {headline && <StyledHeadline tag={headlineTag} id={htmlId} content={headline} rootStyles={headlineStyles} />}
      {subHeadline && <StyledSubheadline content={subHeadline} rootStyles={subHeadlineStyles} />}
      {elements && !!elements.length && (
        <Stack
          className={"scha__cmssection__sub-elements"}
          sx={{
            gap: 3,
          }}
        >
          {elements.map((el, index) => renderCMSComponent(el, undefined, index))}
        </Stack>
      )}
    </RootElement>
  );
};

type HeadlineProps = {
  tag: string | undefined;
  className?: string;
  id?: string;
  content: string;
};

const RootElement = makeExternallyStyledBaseElement("div");

const BaseHeadline = styled(({ tag, id, content, className }: HeadlineProps) => {
  const Tag = (tag?.trim() ?? "p") as keyof JSX.IntrinsicElements;
  return <Tag id={id} className={className} dangerouslySetInnerHTML={createMarkup(content)} />;
})({ margin: 0 });

const StyledHeadline = makeExternallyStyledElement(
  styled(BaseHeadline)(() => ({
    ...getFontStyle(FontSize.HeadlineMedium),
    fontWeight: "bold",
  })),
);

const BaseSubheadline = styled(({ content, className }: Pick<HeadlineProps, "content" | "className">) => {
  return <p className={className} dangerouslySetInnerHTML={createMarkup(content)} />;
})(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: 0,
}));
const StyledSubheadline = makeExternallyStyledElement(BaseSubheadline);
